// assets/collection-tag-pills.js
// Handles tag pill filtering integration with the theme's facet filter system

/**
 * Tag filters use /collections/{handle}/{tag+tag2}. Product-in-collection URLs use
 * /collections/{handle}/products/{product-handle} — the third segment must not be treated as tags.
 * @param {string} pathname
 * @returns {string[]}
 */
function getActiveCollectionTagsFromPath(pathname) {
  const pathParts = pathname.split('/').filter((part) => part.length > 0);
  if (pathParts[0] !== 'collections' || pathParts.length < 3) return [];
  if (pathParts[2] === 'products') return [];
  return pathParts[2].split('+').filter(Boolean);
}

class CollectionTagPills {
  constructor() {
    this.facetFilters = document.querySelector('facet-filters');
    this.pillsContainer = document.querySelector('.collection-banner__tags');
    this.form = document.getElementById('facets');
    this.results = document.getElementById('filter-results');
    
    if (!this.pillsContainer) {
      console.log('[Tag Pills] No pills container found, exiting');
      return;
    }
    
    // Verify facet-filters element exists
    if (!this.facetFilters) {
      console.warn('[Tag Pills] facet-filters element not found');
    } else {
      console.log('[Tag Pills] facet-filters element found, applyFilters method available:', typeof this.facetFilters.applyFilters === 'function');
    }
    
    this.init();
  }

  init() {
    console.log('[Tag Pills] Initializing');
    
    // Add click listeners to all pills
    this.pillsContainer.addEventListener('click', this.handlePillClick.bind(this));
    
    // Listen for filter updates to sync pill states
    document.addEventListener('on:facet-filters:updated', this.syncPillStates.bind(this));
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.syncPillStates(), 50);
    });
    
    // Listen for active filter removals (individual tag removal from active filters)
    if (this.facetFilters) {
      const activeFilters = this.facetFilters.querySelector('.facets__active-filters');
      if (activeFilters) {
        activeFilters.addEventListener('click', (evt) => {
          if (evt.target.tagName === 'A' && evt.target.classList.contains('active-filter')) {
            // Sync pills after filter removal
            setTimeout(() => this.syncPillStates(), 100);
          }
        });
      }
      
      // Listen for clear all filters
      const clearAllBtn = this.facetFilters.querySelector('.js-clear-all');
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', () => {
          // Small delay to let the filter system clear first
          setTimeout(() => this.syncPillStates(), 100);
        });
      }
    }
    
    // Initial sync based on URL
    this.syncPillStates();
    
    console.log('[Tag Pills] Initialization complete');
  }

  handlePillClick(evt) {
    const pill = evt.target.closest('.collection-tag-pill');
    if (!pill) return;
    
    // If this is a collection link pill, let it navigate normally
    if (pill.classList.contains('collection-tag-pill--link') || pill.tagName === 'A') {
      console.log('[Tag Pills] Collection link pill clicked, allowing navigation');
      return; // Don't prevent default, let the link work normally
    }

    // "All" pill: clear collection tag filters (base collection URL, no /tag segment)
    if (pill.classList.contains('collection-tag-pill--all')) {
      evt.preventDefault();
      evt.stopPropagation();
      const currentUrl = new URL(window.location.href);
      const pathParts = currentUrl.pathname.split('/').filter((part) => part.length > 0);
      let basePath = '';
      if (pathParts[0] === 'collections' && pathParts.length >= 2) {
        basePath = `/collections/${pathParts[1]}`;
      } else {
        console.warn('[Tag Pills] Could not resolve collection base path for "all" pill');
        return;
      }
      const searchParams = new URLSearchParams(currentUrl.search);
      searchParams.delete('page');
      const queryString = searchParams.toString();
      const newUrl = queryString ? `${basePath}?${queryString}` : basePath;
      if (newUrl === currentUrl.pathname + (currentUrl.search || '')) {
        return;
      }
      this.applyTagFilters(newUrl, evt);
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();

    const tag = pill.dataset.tag;
    const isActive = pill.classList.contains('active');

    if (!tag) {
      console.warn('[Tag Pills] No tag data attribute found');
      return;
    }
    
    console.log(`[Tag Pills] Clicked tag: ${tag}, isActive: ${isActive}`);
    
    // Get current URL and extract collection path and tags
    const currentUrl = new URL(window.location.href);
    const pathParts = currentUrl.pathname.split('/').filter((part) => part.length > 0);

    // Extract base collection path (e.g., /collections/produit-dhygiene-naturel)
    let basePath = '';
    let currentTags = [];

    if (pathParts[0] === 'collections' && pathParts.length >= 2) {
      basePath = `/collections/${pathParts[1]}`;
      currentTags = getActiveCollectionTagsFromPath(currentUrl.pathname);
    }
    
    console.log('[Tag Pills] Current tags:', currentTags);

    // Single-select: one tag filter at a time; re-click active pill clears (same as "Tous")
    let newTags;
    if (isActive) {
      newTags = [];
      console.log('[Tag Pills] Clearing tag filter (deselect)');
    } else {
      newTags = [tag];
      console.log('[Tag Pills] Selecting single tag (replacing any previous)');
    }

    // Build new URL path
    let newPath = basePath;
    if (newTags.length > 0) {
      newPath += '/' + newTags.join('+');
    }

    // Preserve existing query parameters (like sort_by)
    const searchParams = new URLSearchParams(currentUrl.search);
    // Remove page parameter when filtering (reset to page 1)
    searchParams.delete('page');

    const queryString = searchParams.toString();
    const newUrl = queryString ? `${newPath}?${queryString}` : newPath;

    console.log('[Tag Pills] New URL:', newUrl);

    this.setFilterModePillVisualState(newTags);

    // Use AJAX to fetch and update the page
    this.applyTagFilters(newUrl, evt);
  }

  /**
   * Filter-mode pills only (not subcollection links). Reflects selected tag handles in UI before URL push.
   * @param {string[]} selectedTags — expect [] or one handle
   */
  setFilterModePillVisualState(selectedTags) {
    if (!this.pillsContainer) return;
    if (this.pillsContainer.querySelector('.collection-tag-pill--link')) return;

    this.pillsContainer.querySelectorAll('.collection-tag-pill').forEach((p) => {
      if (p.classList.contains('collection-tag-pill--link') || p.tagName === 'A') return;

      if (p.classList.contains('collection-tag-pill--all')) {
        const allOn = selectedTags.length === 0;
        p.classList.toggle('active', allOn);
        p.setAttribute('aria-pressed', allOn ? 'true' : 'false');
        return;
      }

      const t = p.dataset.tag;
      if (!t) return;
      const on = selectedTags.length > 0 && selectedTags.includes(t);
      p.classList.toggle('active', on);
      p.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  async applyTagFilters(newUrl, evt) {
    try {
      // Set loading state
      if (this.results) {
        this.results.classList.add('is-loading');
      }
      
      // Try to get section ID from form, or find it from the main-collection-products section
      let sectionId = null;
      if (this.form && this.form.dataset.filterSectionId) {
        sectionId = this.form.dataset.filterSectionId;
      } else {
        // Try to find the section ID from the page structure
        const mainSection = document.querySelector('[id*="shopify-section"][id*="collection-products"]');
        if (mainSection) {
          sectionId = mainSection.id.replace('shopify-section-', '');
        }
      }
      
      // Build fetch URL with section ID for section rendering
      const urlObj = new URL(newUrl, window.location.origin);
      if (sectionId) {
        urlObj.searchParams.set('section_id', sectionId);
      }
      
      const fetchUrl = urlObj.toString();
      console.log('[Tag Pills] Fetching:', fetchUrl);
      
      // Fetch the filtered content
      const response = await fetch(fetchUrl, { method: 'GET' });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Update the product results
        const newResults = doc.getElementById('filter-results');
        if (newResults && this.results) {
          this.results.innerHTML = newResults.innerHTML;
          console.log('[Tag Pills] Updated product results');
        }
        
        // Update the facet filters form if it exists
        const newForm = doc.getElementById('facets');
        if (newForm && this.form) {
          // Check if we're in horizontal layout on desktop
          const isHorizontalLayout = this.facetFilters?.closest('.main-products-grid__filters-top') && window.innerWidth >= 1024;
          
          if (isHorizontalLayout) {
            // In horizontal layout, preserve closed state of filters
            // Store current open state before updating
            const openFilterIds = [];
            this.form.querySelectorAll('details-disclosure > details[open]').forEach(detail => {
              // Only preserve active filters section state
              if (detail.closest('.facets__active-filters')) {
                openFilterIds.push(detail.id);
              }
            });
            
            // Update form HTML
            this.form.innerHTML = newForm.innerHTML;
            
            // Force all filter dropdowns to be closed (except active filters)
            this.form.querySelectorAll('details-disclosure > details').forEach(detail => {
              if (detail.closest('.facets__filters')) {
                detail.removeAttribute('open');
              } else if (openFilterIds.includes(detail.id)) {
                detail.setAttribute('open', '');
              }
            });
            
            console.log('[Tag Pills] Updated filters form (horizontal layout - filters closed)');
          } else {
            // Normal update for mobile/sidebar layout
            this.form.innerHTML = newForm.innerHTML;
            console.log('[Tag Pills] Updated filters form');
          }
        }
        
        // Update the URL in the browser
        window.history.pushState({ path: newUrl }, '', newUrl);
        
        // Sync pill states based on new URL
        this.syncPillStates();
        
        // Reinitialize lazy-loaded images if the function exists
        if (typeof initLazyImages === 'function') {
          initLazyImages();
        }
        
        // Dispatch update event for other components
        document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
        
        console.log('[Tag Pills] Tag filter applied successfully');
      } else {
        console.error('[Tag Pills] Response not OK:', response.status);
        // Fallback to page reload if AJAX fails
        window.location.href = newUrl;
      }
    } catch (error) {
      console.error('[Tag Pills] Error applying tag filters:', error);
      // Fallback to page reload on error
      window.location.href = newUrl;
    } finally {
      if (this.results) {
        this.results.classList.remove('is-loading');
      }
    }
  }

  syncPillStates() {
    if (!this.pillsContainer) return;
    
    // Check if we have collection link pills (they don't need syncing)
    const hasCollectionLinks = this.pillsContainer.querySelector('.collection-tag-pill--link');
    if (hasCollectionLinks) {
      console.log('[Tag Pills] Collection links detected, skipping sync');
      return;
    }
    
    const url = new URL(window.location.href);
    const activeTags = getActiveCollectionTagsFromPath(url.pathname);

    console.log('[Tag Pills] Syncing pills with active tags:', activeTags);

    // Only hide pills that cannot match current grid; on product pages etc. #filter-results is absent — return null and keep pills visible
    const availableTags = this.getAvailableTagsFromProducts();
    const canApplyAvailabilityFilter = availableTags !== null;
    console.log('[Tag Pills] Available tags from visible products:', availableTags, 'applyAvailability:', canApplyAvailabilityFilter);
    
    // Update all pills based on active tags and availability
    const pills = this.pillsContainer.querySelectorAll('.collection-tag-pill');
    pills.forEach(pill => {
      const tag = pill.dataset.tag;
      const isLinkPill = pill.classList.contains('collection-tag-pill--link') || pill.tagName === 'A' && !tag;

      // Link pills (subcollections): never hide, no active state syncing
      if (isLinkPill) {
        pill.style.display = '';
        pill.removeAttribute('disabled');
        return;
      }

      // "All" pill: active when no tag segment in URL (shows entire subcollection set)
      if (pill.classList.contains('collection-tag-pill--all')) {
        if (activeTags.length === 0) {
          pill.classList.add('active');
          pill.setAttribute('aria-pressed', 'true');
        } else {
          pill.classList.remove('active');
          pill.setAttribute('aria-pressed', 'false');
        }
        pill.style.display = '';
        pill.removeAttribute('disabled');
        return;
      }

      const isAvailable = canApplyAvailabilityFilter && availableTags.has(tag);

      // Update active state
      if (activeTags.includes(tag)) {
        pill.classList.add('active');
        pill.setAttribute('aria-pressed', 'true');
      } else {
        pill.classList.remove('active');
        pill.setAttribute('aria-pressed', 'false');
      }

      // Show/hide pill based on availability in current results (collection grid only)
      if (!canApplyAvailabilityFilter || isAvailable || activeTags.includes(tag)) {
        pill.style.display = '';
        pill.removeAttribute('disabled');
      } else {
        pill.style.display = 'none';
      }
    });
  }
  
  /**
   * @returns {Set<string>|null} null = cannot derive from DOM (do not hide any pills)
   */
  getAvailableTagsFromProducts() {
    if (!this.results) return null;

    const productCards = this.results.querySelectorAll('[data-product-tags]');
    if (productCards.length === 0) return null;

    const availableTags = new Set();
    let sawAnyTag = false;

    productCards.forEach((card) => {
      const tagsAttr = card.dataset.productTags;
      if (tagsAttr && tagsAttr.trim().length > 0) {
        sawAnyTag = true;
        const tags = tagsAttr.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
        tags.forEach((tag) => availableTags.add(tag));
      }
    });

    if (!sawAnyTag) return null;

    return availableTags;
  }
}

// Initialize when DOM is ready and custom elements are defined
async function initCollectionTagPills() {
  console.log('[Tag Pills] Starting initialization...');
  
  // Wait for the facet-filters custom element to be defined
  if (customElements.get('facet-filters')) {
    console.log('[Tag Pills] Waiting for facet-filters to be defined...');
    await customElements.whenDefined('facet-filters');
    console.log('[Tag Pills] facet-filters is defined');
  } else {
    console.warn('[Tag Pills] facet-filters custom element not registered');
  }
  
  // Small delay to ensure the element is fully initialized
  setTimeout(() => {
    console.log('[Tag Pills] Creating CollectionTagPills instance');
    new CollectionTagPills();
  }, 150);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionTagPills);
} else {
  initCollectionTagPills();
}
